import { useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useVerifyTwoFactorMutation } from '../../features/auth/authApi';
import { otpSchema } from '../../lib/validators/authSchemas';
import { getRoleHomePath } from '../../routes/roleHome';
import { authErrorMessage } from '../../lib/authErrorMessage';
import OtpInput from '../../components/auth/OtpInput';
import Button from '../../components/ui/Button';

/**
 * Second step of the Admin / Super Admin login flow, reached from Login.jsx
 * when POST /auth/login responds with requires_2fa: true. The API doesn't
 * document a separate "resend 2fa code" endpoint — a fresh code only comes
 * from logging in again, so that's what "didn't get a code" points back to.
 */
export default function TwoFactor() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');

  const [verifyTwoFactor, { isLoading: verifying }] = useVerifyTwoFactorMutation();

  if (!email) {
    return (
      <div className="text-center">
        <p className="text-sm text-foreground-muted mb-4">
          We couldn't find a sign-in in progress. Please log in again.
        </p>
        <Link to="/login"><Button className="w-full">Back to login</Button></Link>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const parsed = otpSchema.safeParse({ otp });
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    try {
      const result = await verifyTwoFactor({ email, otp }).unwrap();
      const roles = result?.user?.roles ?? result?.user?.role ?? [];
      navigate(getRoleHomePath(roles), { replace: true });
    } catch (err) {
      const status = err?.status;
      setError(
        authErrorMessage(err, {
          fallback:
            status === 410
              ? 'That code has expired — log in again for a new one.'
              : status === 403
                ? 'This account is suspended.'
                : 'Invalid code.',
        })
      );
    }
  };

  return (
    <div className="text-center">
      <h1 className="text-xl font-semibold text-foreground mb-1">Two-factor verification</h1>
      <p className="text-sm text-foreground-muted mb-6">
        Admin accounts require a second step. Enter the 6-digit code sent to <span className="text-foreground">{email}</span>.
      </p>

      <form onSubmit={onSubmit} className="space-y-6">
        <OtpInput value={otp} onChange={setOtp} error={error} />

        <Button type="submit" className="w-full" isLoading={verifying}>
          Verify and sign in
        </Button>
      </form>

      <p className="text-sm text-foreground-muted mt-6">
        Didn't get a code?{' '}
        <Link to="/login" className="text-accent hover:underline">Log in again for a new one</Link>
      </p>
    </div>
  );
}
